/**
 * Menu da plataforma Marketing — a plataforma única: Início · Marketing Studio ·
 * Inteligência · Marca · Biblioteca · Product Studio. Os apps do Product Studio
 * (Canvas, Workflow, Lookbook, Meus modelos, Brandbook e a Galeria) também
 * abrem aqui, em rotas /marketing/*, sem sair do menu; as rotas antigas do
 * Product Studio continuam valendo.
 *
 * Item com `to` navega; item sem `to` ainda não tem tela nem backend e aparece
 * desabilitado com "Em breve" — cada um entra num PR próprio (um assunto por PR).
 * Item com `module` segue o módulo da conta, como no menu do Product Studio:
 * oculto some, em desenvolvimento ou manutenção fica desabilitado.
 */
export type MarketingNavItemId =
  | 'home'
  | 'imageAd'
  | 'videoAd'
  | 'batch'
  | 'competitors'
  | 'references'
  | 'brandDna'
  | 'products'
  | 'meta'
  | 'ads'
  | 'gallery'
  | 'projects'
  | 'publishing'
  | 'canvas'
  | 'workflow'
  | 'lookbook'
  | 'myModels'
  | 'brandbook';

export type MarketingIconName =
  | 'home'
  | 'image'
  | 'video'
  | 'batch'
  | 'competitors'
  | 'references'
  | 'dna'
  | 'products'
  | 'meta'
  | 'ads'
  | 'gallery'
  | 'projects'
  | 'publishing'
  | 'canvas'
  | 'workflow'
  | 'lookbook'
  | 'models'
  | 'brandbook';

export interface MarketingNavItem {
  id: MarketingNavItemId;
  icon: MarketingIconName;
  /** Rota da tela; ausente = "Em breve". */
  to?: string;
  /** Slug do módulo da conta (useModules) que libera o item. */
  module?: string;
}

export interface MarketingNavGroup {
  id: 'general' | 'marketingStudio' | 'intelligence' | 'brand' | 'library' | 'productStudio';
  /** Sem título de grupo (o Início fica sozinho no topo). */
  untitled?: boolean;
  items: MarketingNavItem[];
}

export const MARKETING_NAV: MarketingNavGroup[] = [
  { id: 'general', untitled: true, items: [{ id: 'home', icon: 'home', to: '/marketing' }] },
  {
    id: 'marketingStudio',
    items: [
      { id: 'imageAd', icon: 'image', to: '/marketing/anuncio-imagem' },
      { id: 'videoAd', icon: 'video', to: '/marketing/anuncio-video' },
      { id: 'batch', icon: 'batch', to: '/marketing/variacoes-em-lote' },
    ],
  },
  {
    id: 'intelligence',
    items: [
      { id: 'competitors', icon: 'competitors', to: '/marketing/concorrentes' },
      { id: 'references', icon: 'references', to: '/marketing/referencias' },
    ],
  },
  {
    id: 'brand',
    items: [
      { id: 'brandDna', icon: 'dna', to: '/marketing/dna' },
      { id: 'products', icon: 'products', to: '/marketing/produtos' },
      { id: 'meta', icon: 'meta', to: '/marketing/meta' },
    ],
  },
  {
    id: 'library',
    items: [
      { id: 'ads', icon: 'ads', to: '/marketing/anuncios' },
      { id: 'gallery', icon: 'gallery', to: '/marketing/galeria', module: 'gallery' },
      { id: 'projects', icon: 'projects' },
      { id: 'publishing', icon: 'publishing' },
    ],
  },
  {
    id: 'productStudio',
    items: [
      { id: 'canvas', icon: 'canvas', to: '/marketing/canvas', module: 'moodboard' },
      { id: 'workflow', icon: 'workflow', to: '/marketing/workflow', module: 'workflow' },
      { id: 'lookbook', icon: 'lookbook', to: '/marketing/lookbook', module: 'lookbook' },
      { id: 'myModels', icon: 'models', to: '/marketing/modelos', module: 'my_models' },
      { id: 'brandbook', icon: 'brandbook', to: '/marketing/brandbook', module: 'brandbook' },
    ],
  },
];

/**
 * Menu "O que você quer criar?" (atalho C). Opções com `to` reaproveitam
 * ferramentas que já existem, montadas dentro do segmento Marketing.
 */
export type CreateOptionId =
  | 'staticAd'
  | 'carousel'
  | 'adaptFormats'
  | 'ugc'
  | 'talkingHead'
  | 'animate'
  | 'recreate'
  | 'batch'
  | 'playground';

export interface CreateOption {
  id: CreateOptionId;
  to?: string;
}

export interface CreateGroup {
  id: 'image' | 'video' | 'reference' | 'scratch';
  options: CreateOption[];
}

export const CREATE_MENU: CreateGroup[] = [
  { id: 'image', options: [{ id: 'staticAd', to: '/marketing/anuncio-imagem' }, { id: 'carousel' }, { id: 'adaptFormats' }] },
  { id: 'video', options: [{ id: 'ugc' }, { id: 'talkingHead' }, { id: 'animate', to: '/marketing/animar-imagem' }] },
  { id: 'reference', options: [{ id: 'recreate', to: '/marketing/concorrentes' }, { id: 'batch', to: '/marketing/variacoes-em-lote' }] },
  { id: 'scratch', options: [{ id: 'playground', to: '/marketing/canvas' }] },
];

export const isMarketingNavActive = (item: MarketingNavItem, pathname: string): boolean =>
  !!item.to &&
  (item.to === '/marketing'
    ? pathname === '/marketing' || pathname === '/marketing/'
    : pathname === item.to || pathname.startsWith(`${item.to}/`));
